<script setup>
import { computed } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import UiInput from '@/Components/UI/UiInput.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import PhoneInput from '@/Components/UI/PhoneInput.vue'
import CustomerChannels from '@/Components/CustomerChannels.vue'

const { t } = useI18n()

const props = defineProps({
  owner:  { type: Object, required: true },
  errors: { type: Object, default: () => ({}) }
})

const form = useForm({
  name:                        props.owner.name ?? '',
  first_name:                  props.owner.first_name ?? '',
  last_name:                   props.owner.last_name ?? '',
  contact_phone:               props.owner.contact_phone ?? '',
  contact_channels_attributes: props.owner.channels ?? []
})

function submit() {
  form.transform((d) => ({ owner: d })).patch('/profile', { preserveScroll: true })
}

const err = (k) => form.errors[k] || (props.errors[k] && props.errors[k][0])

// Nested channel validation errors arrive keyed like
// "contact_channels.custom_label" — surface them by the channels block so a
// failed save (e.g. an "other" channel with no label) isn't a silent no-op.
const channelErrors = computed(() => {
  const out = []
  for (const [key, val] of Object.entries(props.errors || {})) {
    if (!key.startsWith('contact_channels')) continue
    const attr = key.split('.').pop().replace(/_/g, ' ')
    const label = attr.charAt(0).toUpperCase() + attr.slice(1)
    for (const msg of Array.isArray(val) ? val : [val]) out.push(`${label} ${msg}`)
  }
  return out
})

const labelCls = 'block text-sm font-medium text-[color:var(--inkSoft)] mb-1'
</script>

<template>
  <form
    id="portal-profile-form"
    class="space-y-4"
    @submit.prevent="submit"
  >
    <div>
      <label :class="labelCls">{{ t('portal_profile_form.business_name') }} *</label>
      <UiInput
        v-model="form.name"
        required
        :error="err('name')"
      />
    </div>

    <div class="grid grid-cols-2 gap-4">
      <div>
        <label :class="labelCls">{{ t('portal_profile_form.first_name') }}</label>
        <UiInput
          v-model="form.first_name"
          :error="err('first_name')"
        />
      </div>
      <div>
        <label :class="labelCls">{{ t('portal_profile_form.last_name') }}</label>
        <UiInput
          v-model="form.last_name"
          :error="err('last_name')"
        />
      </div>
    </div>

    <div>
      <label :class="labelCls">{{ t('portal_profile_form.phone') }}</label>
      <PhoneInput v-model="form.contact_phone" />
    </div>

    <div>
      <label :class="labelCls">{{ t('portal_profile_form.email') }}</label>
      <p class="text-sm text-[color:var(--inkSoft)]">
        {{ owner.contact_email }}
      </p>
    </div>

    <div class="border-t border-[color:var(--line)] pt-4">
      <label :class="labelCls">{{ t('portal_profile_form.channels') }}</label>
      <CustomerChannels v-model="form.contact_channels_attributes" />
      <ul
        v-if="channelErrors.length"
        class="mt-2 space-y-1"
      >
        <li
          v-for="(msg, i) in channelErrors"
          :key="i"
          class="text-sm text-[color:var(--danger)]"
        >
          {{ msg }}
        </li>
      </ul>
    </div>

    <div class="pt-2">
      <UiButton
        type="submit"
        form="portal-profile-form"
        variant="primary"
        :loading="form.processing"
      >
        {{ form.processing ? t('portal_profile_form.saving') : t('portal_profile_form.save') }}
      </UiButton>
    </div>
  </form>
</template>
